import { useTranslation } from "react-i18next";
import { Button, Drawer, Toast } from "@/ui-kit";
import { Form } from "@/components/controls";
import { FormProvider, useForm } from "react-hook-form";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ClientsRepository } from "@/api/clients/repository";
import { ClientsService } from "@/api/clients/service";
import type { IClient } from "@/types/users-clients";
import type { IClientDetailsFormData } from "@/pages/Settings/types";
import { AddressControl } from "@/components/controls/AddressControl";
import { addressSchema } from "@/utils/validation";
import { AddressUtils } from "@/utils/address";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

type IProps = {
  client: IClient;
  onSave: (updatedClient: IClient) => void;
  onDismiss: () => void;
};

const EditModal = ({ client, onSave, onDismiss }: IProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);

  const schema = useMemo(() => {
    return z.object({
      address: addressSchema,
    });
  }, []);

  const form = useForm<IClientDetailsFormData>({
    defaultValues: {
      address: AddressUtils.getNormalized(client.address),
    },
    resolver: zodResolver(schema),
  });

  const { handleSubmit } = form;

  const repository = useMemo(() => {
    return new ClientsRepository(new ClientsService(), { abort: true });
  }, []);

  const onSubmit = useCallback(() => {
    handleSubmit((formData: IClientDetailsFormData) => {
      setLoading(true);

      repository
        .updateClient({
          id: client.id,
          userId: client.userId,
          address: formData.address,
        })
        .then((response) => {
          onSave(response);
          Toast.success({
            title: t("toast.what_updated", {
              what: t("label.address"),
            }),
          });
          onDismiss();
        })
        .catch(
          serverErrorHandler(() => {
            setLoading(false);
          }),
        );
    })();
  }, [
    handleSubmit,
    repository,
    client.id,
    client.userId,
    onSave,
    onDismiss,
    t,
  ]);

  const handleDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  return (
    <FormProvider {...form}>
      <Drawer
        open={true}
        onOpenChange={onDismiss}
        title={t("action.edit_what", { what: t("label.address") })}
        closeOnOutsidePress={false}
        footer={
          <>
            <Button onClick={handleDismiss} variant="ghost" color="layer1">
              {t("action.cancel")}
            </Button>
            <Button onClick={onSubmit} loading={loading}>
              {t("action.save")}
            </Button>
          </>
        }
      >
        <Form>
          <AddressControl label={t("label.address")} name="address" />
        </Form>
      </Drawer>
    </FormProvider>
  );
};

export default EditModal;
